const UNKNOWN_REFUSAL = 'aProblemHasOccuredContactTheAdministrator';

/**
 * The locale key of each refusal of a classroom enroll: `EnrollValidationResult` in
 * Leerplatform.API/Sdb.Learning.Core/Classroom, camelCased as the leerplatform and learning-academy write it. A refusal that
 * reads differently for the participant themself has `{self, other}`.
 */
const ENROLLMENT_VALIDATION_RESULT_KEYS = {
    queued: 'thisScheduleIsFullTheNewRegistrationsWillBeQueued',
    failedCapacityReached: 'capacityReached',
    failedMaxNumRegistrationsReached: {
        self: 'theAllowedNumberOfRegistrationsForMyselfHasBeenReached',
        other: 'theAllowedNumberOfRegistrationsForThisUserHasBeenReached',
    },
    failedConflictingEnrollment: {
        self: 'thisScheduleConflictsWithAnotherScheduleForWhichYouAreRegistered',
        other: 'thisScheduleConflictsWithAnotherScheduleForWhichThisParticipantIsRegistered',
    },
    failedHasTaggedRegistration: 'HasTaggedRegistration',
    failedAlreadyRegistered: {
        self: 'youAreAlreadyRegistered',
        other: 'enrollmentRefusedParticipantAlreadyRegistered',
    },
    failedEnrollmentDeadlinePassed: 'registrationPeriodHasExpired',
    failedPlanningNotActive: 'enrollmentRefusedPlanningNotActive',
    failedPlanningEnded: 'enrollmentRefusedPlanningEnded',
    failedPendingEnrollment: 'enrollmentRefusedPendingEnrollment',
    failedEnrollmentChanged: 'enrollmentRefusedEnrollmentChanged',
    failedTransferSamePlanning: 'enrollmentRefusedTransferSamePlanning',
    failedOtherCourse: 'enrollmentRefusedOtherCourse',
    failedEnrollmentNotTransferable: 'enrollmentRefusedEnrollmentNotTransferable',
    failedPresenceOrResultKnown: 'enrollmentRefusedPresenceOrResultKnown',
    failedUnenrollmentDeadlinePassed: 'enrollmentRefusedUnenrollmentDeadlinePassed',
    failedPaymentHookNotSupported: 'enrollmentRefusedPaymentHookNotSupported',
    failedPaymentProviderUnavailable: 'enrollmentRefusedPaymentProviderUnavailable',
    failedPaymentNotAvailable: 'enrollmentRefusedPaymentNotAvailable',
    failedPaymentRequired: 'enrollmentRefusedPaymentRequired',
    failedPaymentPending: 'enrollmentRefusedPaymentPending',
    failedPaymentNoPage: 'enrollmentRefusedPaymentNoPage',
};

/** ValidateEnroll and ValidateReservationCode answer the enum name, the writes its camelCased name. */
const toCamelCase = (value) => value.charAt(0).toLowerCase() + value.slice(1);

/**
 * The text of a refused classroom enroll, transfer or temporary enrollment, as `localize(key, ...params)`.
 *
 * @param {string|null|undefined} result an `EnrollValidationResult`, camelCased or not
 * @param {{self?: boolean, queuePosition?: number}} [options] whether the participant enrolls themself, and the position
 *   a queued enroll gets
 * @returns {{key: string, params: unknown[]}}
 */
export function enrollmentValidationMessage(result, {self = false, queuePosition} = {}) {
    const name = typeof result === 'string' ? toCamelCase(result) : '';
    const keys = Object.hasOwn(ENROLLMENT_VALIDATION_RESULT_KEYS, name)
        ? ENROLLMENT_VALIDATION_RESULT_KEYS[name]
        : null;

    if (!keys) return {key: UNKNOWN_REFUSAL, params: []};

    if (name === 'queued' && queuePosition !== undefined) {
        return {
            key: 'thisScheduleIsFullTheNewRegistrationsWillBeQueuedOnPosition',
            params: ['number', queuePosition],
        };
    }

    if (typeof keys === 'string') return {key: keys, params: []};

    return {key: self ? keys.self : keys.other, params: []};
}

/**
 * The text of a refused reservation code (`ReservationCodeValidationResult`), as `localize(key, ...params)`.
 *
 * @param {string} result the result, camelCased or not
 * @returns {{key: string, params: unknown[]}}
 */
export function reservationCodeMessage(result) {
    return {key: 'enrollReservationError', params: ['status', toCamelCase(result)]};
}

/**
 * The text of the refusal in the body of a failed classroom write, as `localize(key, ...params)`: the
 * `enrollmentValidationResult` of the leerplatform and learning-academy, the `enrollValidationResult` of the Catalog API, or
 * a `reservationCodeValidationResult`.
 *
 * @param {unknown} body the parsed body of the failed response
 * @param {{self?: boolean, queuePosition?: number}} [options] see {@link enrollmentValidationMessage}
 * @returns {{key: string, params: unknown[]}|null} `null` when the body names no refusal
 */
export function refusalMessage(body, options) {
    if (!body || typeof body !== 'object') return null;

    const enrollmentResult = body.enrollmentValidationResult ?? body.enrollValidationResult;

    if (enrollmentResult) return enrollmentValidationMessage(enrollmentResult, options);

    if (body.reservationCodeValidationResult) {
        return reservationCodeMessage(body.reservationCodeValidationResult);
    }

    return null;
}
